import classNames from "classnames";
import { ButtonVariant, CoButton } from "components/CoButton";
import useHotkey from "hooks/useHotkey";
import useIsMac from "hooks/useIsMac";
import useLabels from "hooks/useLabels";
import useModalStack from "hooks/useModalStack/useModalStack";
import { useSearch } from "hooks/useSearch";
import { useSidebar } from "hooks/useSidebar";
import { useAppSelector } from "hooks/useStore";
import { useRouter } from "next/router";
import CommandOrControlKIconGroup from "./components/CommandOrControlKIconGroup";
import { NEW_THREAD_MODAL_ID } from "./components/NewThreadModal";
type NewThreadButtonProps = {
  asSidebarNavItem?: boolean;
  isCollapsed?: boolean;
};

function NewThreadButton({
  asSidebarNavItem = false,
  isCollapsed = false,
}: NewThreadButtonProps) {
  const router = useRouter();
  const [labels] = useLabels("features.new-thread-button");
  const { openModal } = useModalStack({
    id: NEW_THREAD_MODAL_ID,
    blocksScroll: false,
  });
  const { toggleSidebar } = useSidebar();
  const { focusSearchInputBox } = useSearch();
  const isHomePage = router.pathname === "/" || router.pathname === "/search";
  const isMobile = useAppSelector((state) => state.setting.isMobile);

  const handleOpenModal = () => {
    if (!isCollapsed && isMobile) {
      toggleSidebar();
    }

    if (isHomePage) {
      focusSearchInputBox(true);
      return;
    }
    openModal();
  };

  // Configure hotkey to open the modal
  const isMac = useIsMac();
  const hotkeys = isMac ? ["Meta", "k"] : ["Control", "k"];
  useHotkey(hotkeys, handleOpenModal);

  return (
    <>
      {asSidebarNavItem && !isCollapsed ? (
        <div className="w-full flex px-2">
          <button
            style={{ fontVariantLigatures: "none" }}
            className="group flex flex-1 items-center pl-1 pr-2 h-9 rounded-xl gap-2 ring-transparent focus:outline-hidden hover:bg-bg-faint"
            onClick={handleOpenModal}
          >
            <div className="flex shrink-0 items-center justify-center w-7 h-7 rounded-lg bg-accent-bg-subtle border border-b-2 group-active:border-b border-[var(--color-component-button-secondary-border)]">
              <i className="text-xl">
                <i className="w-4 h-4 text-accent-emphasis icon-plus" />
              </i>
            </div>
            <span className="sm-md text-fg-base w-full text-left">
              {labels["new-thread"]}
            </span>
            <span className="tiny-md text-fg-muted">
              {isMac ? "⌘+K" : "Ctrl+K"}
            </span>
          </button>
        </div>
      ) : (
        <CoButton
          onClick={handleOpenModal}
          icon="icon-plus"
          label={isCollapsed ? null : labels["new-thread"]}
          size="sm"
          className={classNames(isCollapsed ? "" : "w-full ml-4 mr-4")}
          variant={ButtonVariant.Secondary}
          iconRight={
            isCollapsed ? undefined : (
              <div className="ml-2">
                <CommandOrControlKIconGroup variant="secondary" size="sm" />
              </div>
            )
          }
        />
      )}
    </>
  );
}

export default NewThreadButton;
